/**
 * Users API Module
 * Handles all user-related API calls
 */

import apiClient, { extractData } from '../api-client';

export const userApi = {
  /**
   * List all users with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20, max: 100)
   * @param {string} params.search - Search term
   * @param {string} params.role - Filter by role
   * @param {boolean} params.is_active - true: chỉ tài khoản đang hoạt động
   *   (bỏ trống/null: lấy cả tài khoản đã vô hiệu hoá)
   * @returns {Promise<Object>} Paginated response with users
   */
  list(params) {
    return apiClient.get('/users', { params }).then(extractData);
  },

  /**
   * Get user by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} User response
   */
  getById(userId) {
    return apiClient.get(`/users/${userId}`).then(extractData);
  },

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @param {string} userData.username - Username
   * @param {string} userData.password - Password
   * @param {string} userData.full_name - Full name
   * @param {string} userData.email - Email
   * @param {string} userData.phone - Phone number
   * @param {number} userData.role_id - Role ID
   * @returns {Promise<Object>} Created user response
   */
  create(userData) {
    return apiClient.post('/users', userData).then(extractData);
  },

  /**
   * Update user
   * @param {number} userId - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise<Object>} Updated user response
   */
  update(userId, userData) {
    return apiClient.put(`/users/${userId}`, userData).then(extractData);
  },

  /**
   * Vô hiệu hoá user (soft delete: backend chỉ set is_active=false, không xoá
   * bản ghi vì orders/audit_logs còn tham chiếu user_id)
   * @param {number} userId - User ID
   * @returns {Promise<Object>} Message response
   */
  delete(userId) {
    return apiClient.delete(`/users/${userId}`).then(extractData);
  },

  /**
   * Change a user's password (admin only)
   * @param {number} userId - User ID
   * @param {string} newPassword - New password (min 6 chars)
   * @returns {Promise<Object>} Message response
   */
  changePassword(userId, newPassword) {
    return apiClient
      .put(`/users/${userId}/password`, { new_password: newPassword })
      .then(extractData);
  },

  /** Đặt lại 2FA (mất điện thoại) — admin */
  resetTwoFactor(userId) {
    return apiClient.post(`/users/${userId}/2fa/reset`).then(extractData);
  },

  /**
   * List active users assignable as processing staff
   * @returns {Promise<Array>} List of { id, full_name, username }
   */
  listAssignable() {
    return apiClient.get('/users/assignable').then(extractData);
  },

  /**
   * Danh mục role (admin/staff/accountant) cho form tạo/sửa nhân
   * viên — thay hardcode id ở frontend vì role mới (vd accountant) có id
   * auto-increment khác nhau giữa các môi trường.
   * @returns {Promise<Array>} List of { id, name, description }
   */
  getRoles() {
    return apiClient.get('/users/roles').then(extractData);
  }
};
